import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  AssignmentTypeEnum,
  AssignmentHistoryDetailsModel,
  AssignmentHistoryResponse,
} from 'api/adminApiClient';
import { getFullName } from 'utils/personUtils';
import { getWorkerTypeLabel } from 'utils/workerUtils';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import HistoryLog from '../../../HistoryLog';
import HistoryField from '../../../HistoryField';
import { getAssignmentTypeLabel } from 'utils/assignmentUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getHistoryContractorCompaniesFile } from 'redux/reducers/contractorCompanySlice';
import { getWorkerHistoryFile } from 'redux/reducers/workerSlice';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyDetails: AssignmentHistoryDetailsModel;
  historyId: string;
};

export default function GeneralInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();

  //Worker information
  const hasWorkerInfoFirstNameChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.firstName || '',
    newData?.workerInfo?.workerInfo?.firstName || '',
  );
  const hasWorkerInfoLastNameChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.lastName || '',
    newData?.workerInfo?.workerInfo?.lastName || '',
  );
  const hasWorkerInfoEmailChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.email || '',
    newData?.workerInfo?.workerInfo?.email || '',
  );
  const hasWorkerInfoPhoneNumberChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.phoneNumber || '',
    newData?.workerInfo?.workerInfo?.phoneNumber || '',
  );
  const hasWorkerInfoChanged =
    hasWorkerInfoFirstNameChanged ||
    hasWorkerInfoLastNameChanged ||
    hasWorkerInfoEmailChanged ||
    hasWorkerInfoPhoneNumberChanged;

  //Worker home address
  const hasWorkerCountryChanged = !isEqual(
    oldData?.workerInfo?.location?.country || '',
    newData?.workerInfo?.location?.country || '',
  );
  const hasWorkerStateChanged = !isEqual(
    oldData?.workerInfo?.location?.state ||
      oldData?.workerInfo?.location?.internationalStateName ||
      '',
    newData?.workerInfo?.location?.state ||
      newData?.workerInfo?.location?.internationalStateName ||
      '',
  );
  const hasWorkerCityChanged = !isEqual(
    oldData?.workerInfo?.location?.city ||
      oldData?.workerInfo?.location?.internationalCityName ||
      '',
    newData?.workerInfo?.location?.city ||
      newData?.workerInfo?.location?.internationalCityName ||
      '',
  );
  const hasWorkerAddressChanged = !isEqual(
    oldData?.workerInfo?.location?.address || '',
    newData?.workerInfo?.location?.address || '',
  );
  const hasWorkerZipChanged = !isEqual(
    oldData?.workerInfo?.location?.zipCode || '',
    newData?.workerInfo?.location?.zipCode || '',
  );
  const hasWorkerHomeAddressChanged =
    hasWorkerCountryChanged ||
    hasWorkerStateChanged ||
    hasWorkerCityChanged ||
    hasWorkerAddressChanged ||
    hasWorkerZipChanged;

  // Contractor company information
  const hasContractorCompanyName = !isEqual(
    oldData?.contractorCompanyInfo?.companyName || '',
    newData?.contractorCompanyInfo?.companyName || '',
  );
  const hasRepresentativeContactFirstNameChanged = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.firstName || '',
    newData?.contractorCompanyInfo?.representativeContact?.firstName || '',
  );
  const hasRepresentativeContactLastNameChanged = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.lastName || '',
    newData?.contractorCompanyInfo?.representativeContact?.lastName || '',
  );
  const hasRepresentativeContactEmailChanged = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.email || '',
    newData?.contractorCompanyInfo?.representativeContact?.email || '',
  );
  const hasRepresentativeContactPhoneNumberChanged = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.phoneNumber || '',
    newData?.contractorCompanyInfo?.representativeContact?.phoneNumber || '',
  );
  const hasContractorTaxId = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.taxId || '',
    newData?.contractorCompanyInfo?.representativeContact?.taxId || '',
  );
  const hasContractorWorkerKey = !isEqual(
    oldData?.contractorCompanyInfo?.representativeContact?.workerFileKey || '',
    newData?.contractorCompanyInfo?.representativeContact?.workerFileKey || '',
  );

  const hasContractorCompanyInformationChanged =
    hasContractorCompanyName ||
    hasRepresentativeContactLastNameChanged ||
    hasRepresentativeContactFirstNameChanged ||
    hasRepresentativeContactEmailChanged ||
    hasRepresentativeContactPhoneNumberChanged ||
    hasContractorTaxId ||
    hasContractorWorkerKey;

  // Business address
  const hasCompanyCountryChanged = !isEqual(
    oldData?.contractorCompanyInfo?.location?.country || '',
    newData?.contractorCompanyInfo?.location?.country || '',
  );
  const hasCompanyAddressChanged = !isEqual(
    oldData?.contractorCompanyInfo?.location?.address || '',
    newData?.contractorCompanyInfo?.location?.address || '',
  );
  const hasCompanyCityChanged = !isEqual(
    oldData?.contractorCompanyInfo?.location?.city ||
      oldData?.contractorCompanyInfo?.location?.internationalCityName ||
      '',
    newData?.contractorCompanyInfo?.location?.city ||
      newData?.contractorCompanyInfo?.location?.internationalCityName ||
      '',
  );
  const hasCompanyStateChanged = !isEqual(
    oldData?.contractorCompanyInfo?.location?.state ||
      oldData?.contractorCompanyInfo?.location?.internationalStateName ||
      '',
    newData?.contractorCompanyInfo?.location?.state ||
      newData?.contractorCompanyInfo?.location?.internationalStateName ||
      '',
  );
  const hasCompanyZipCodeChanged = !isEqual(
    oldData?.contractorCompanyInfo?.location?.zipCode || '',
    newData?.contractorCompanyInfo?.location?.zipCode || '',
  );
  const hasBusinessAddressChanged =
    hasCompanyAddressChanged ||
    hasCompanyCityChanged ||
    hasCompanyStateChanged ||
    hasCompanyZipCodeChanged ||
    hasCompanyCountryChanged;

  // Worker type
  const hasWorkerTypeChanged = !isEqual(
    oldData?.workerInfo?.workerType || '',
    newData?.workerInfo?.workerType || '',
  );
  const hasWorkerTaxIdChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.taxId || '',
    newData?.workerInfo?.workerInfo?.taxId || '',
  );
  const hasWorkerInfoFileKeyChanged = !isEqual(
    oldData?.workerInfo?.workerInfo?.workerFileKey || '',
    newData?.workerInfo?.workerInfo?.workerFileKey || '',
  );
  const hasWorkerTypeSectionChanged =
    hasWorkerTypeChanged ||
    hasWorkerTaxIdChanged ||
    hasWorkerInfoFileKeyChanged;

  const hasAssignmentTypeChanged = !isEqual(
    historyDetails.oldData?.assignmentType || '',
    historyDetails.newData?.assignmentType || '',
  );

  const handleDownloadContractorCompaniesFile = async (
    fileKey?: string,
    companyId?: string,
  ) => {
    const result = await dispatch(
      getHistoryContractorCompaniesFile({
        companyId,
        fileKey,
      }),
    );

    if (getHistoryContractorCompaniesFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadAgreement = async (
    workerId?: string,
    fileKey?: string,
  ) => {
    const result = await dispatch(
      getWorkerHistoryFile({
        workerId,
        fileKey,
      }),
    );

    if (getWorkerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderAssignmentType = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { assignmentType } = assignment;

    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Assignment type</div>
        </Col>
        {hasAssignmentTypeChanged && (
          <HistoryField
            title="Assignment type"
            value={getAssignmentTypeLabel(assignmentType)}
            dataId="assignment-type-field"
          />
        )}
      </Row>
    );
  };

  const renderWorkerInformation = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { workerInfo } = assignment;

    const sectionName = 'worker-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Worker information</div>
          </Col>
          {hasWorkerInfoFirstNameChanged && (
            <HistoryField
              title="First name"
              value={workerInfo?.workerInfo?.firstName}
              dataId={`${sectionName}-firstName-field`}
            />
          )}
          {hasWorkerInfoLastNameChanged && (
            <HistoryField
              title="Last name"
              value={workerInfo?.workerInfo?.lastName}
              dataId={`${sectionName}-lastName-field`}
            />
          )}
          {hasWorkerInfoEmailChanged && (
            <HistoryField
              title="Email"
              value={workerInfo?.workerInfo?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasWorkerInfoPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={workerInfo?.workerInfo?.phoneNumber}
              dataId={`${sectionName}-phoneNumber-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderWorkerHomeAddress = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { workerInfo } = assignment;

    const sectionName = 'worker-home-address';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Home address</div>
          </Col>
          {hasWorkerCountryChanged && (
            <HistoryField
              title="Country"
              value={workerInfo?.location?.country}
              dataId={`${sectionName}-country-field`}
            />
          )}
          {hasWorkerStateChanged && (
            <HistoryField
              title="State"
              value={
                workerInfo?.location?.state ||
                workerInfo?.location?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}
          {hasWorkerCityChanged && (
            <HistoryField
              title="City"
              value={
                workerInfo?.location?.city ||
                workerInfo?.location?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}
          {hasWorkerAddressChanged && (
            <HistoryField
              title="Address"
              value={workerInfo?.location?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
          {hasWorkerZipChanged && (
            <HistoryField
              title="ZIP"
              value={workerInfo?.location?.zipCode}
              dataId={`${sectionName}-zip-code-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderContractorCompanyInformation = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { contractorCompanyInfo } = assignment;
    const isSCAssignment =
      assignment?.assignmentType === AssignmentTypeEnum.SCAssignment;

    const sectionName = isSCAssignment
      ? 'parent-subcontractor-information'
      : 'contractor-company-information';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              {isSCAssignment
                ? 'Parent subcontractor information'
                : 'Contractor company information'}
            </div>
          </Col>
          {hasContractorCompanyName && (
            <HistoryField
              title={isSCAssignment ? 'Parent SC company' : 'Company'}
              value={contractorCompanyInfo?.companyName}
              dataId={`${sectionName}-company-name-field`}
            />
          )}
          {(hasRepresentativeContactFirstNameChanged ||
            hasRepresentativeContactLastNameChanged) && (
            <HistoryField
              title="Primary contact"
              value={getFullName(
                contractorCompanyInfo?.representativeContact?.firstName,
                contractorCompanyInfo?.representativeContact?.lastName,
              )}
              dataId={`${sectionName}-primary-contact-field`}
            />
          )}
          {hasRepresentativeContactEmailChanged && (
            <HistoryField
              title="Email"
              value={contractorCompanyInfo?.representativeContact?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasRepresentativeContactPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={contractorCompanyInfo?.representativeContact?.phoneNumber}
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
          {hasContractorTaxId && (
            <HistoryField
              title="Tax ID"
              value={contractorCompanyInfo?.representativeContact?.taxId}
              dataId={`${sectionName}-tax-id-field`}
            />
          )}
          {hasContractorWorkerKey && (
            <HistoryField
              title="W9"
              emptyText="No file"
              value={
                contractorCompanyInfo?.representativeContact?.workerFileKey && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleDownloadContractorCompaniesFile(
                        contractorCompanyInfo?.representativeContact
                          ?.workerFileKey,
                        contractorCompanyInfo.companyId,
                      )
                    }
                  >
                    {contractorCompanyInfo?.workerFile?.fileName}
                  </span>
                )
              }
              dataId={`${sectionName}-tax-id-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderBusinessAddressSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { contractorCompanyInfo } = assignment;

    const sectionName = 'business-address';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Business address</div>
          </Col>
          {hasCompanyCountryChanged && (
            <HistoryField
              title="Country"
              value={contractorCompanyInfo?.location?.country}
              dataId={`${sectionName}-country-field`}
            />
          )}
          {hasCompanyStateChanged && (
            <HistoryField
              title="State"
              value={
                contractorCompanyInfo?.location?.state ||
                contractorCompanyInfo?.location?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}
          {hasCompanyCityChanged && (
            <HistoryField
              title="City"
              value={
                contractorCompanyInfo?.location?.city ||
                contractorCompanyInfo?.location?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}
          {hasCompanyAddressChanged && (
            <HistoryField
              title="Address"
              value={contractorCompanyInfo?.location?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
          {hasCompanyZipCodeChanged && (
            <HistoryField
              title="ZIP"
              value={contractorCompanyInfo?.location?.zipCode}
              dataId={`${sectionName}-zip-code-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderWorkerTypeSection = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { workerInfo } = assignment;

    const sectionName = 'worker-type';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Worker type</div>
          </Col>
          {hasWorkerTypeChanged && (
            <HistoryField
              title="Worker type"
              value={getWorkerTypeLabel(workerInfo?.workerType)}
              dataId={`${sectionName}-field`}
            />
          )}
          {hasWorkerTaxIdChanged && (
            <HistoryField
              title="Tax ID"
              value={workerInfo?.workerInfo?.taxId}
              dataId={`${sectionName}-tax-id-field`}
            />
          )}
          {hasWorkerInfoFileKeyChanged && (
            <HistoryField
              title="W9"
              emptyText="No file"
              value={
                workerInfo?.workerFile?.fileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleDownloadAgreement(
                        workerInfo?.workerId,
                        workerInfo?.workerFile?.fileKey,
                      )
                    }
                  >
                    {getFileNameWithoutExt(workerInfo?.workerFile?.fileName)}
                  </span>
                )
              }
              dataId={`${sectionName}-file-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderGeneralInformation = (assignment?: AssignmentHistoryResponse) => {
    return (
      <>
        {hasAssignmentTypeChanged && <>{renderAssignmentType(assignment)}</>}
        {hasWorkerInfoChanged && <>{renderWorkerInformation(assignment)}</>}
        {hasWorkerHomeAddressChanged && (
          <>{renderWorkerHomeAddress(assignment)}</>
        )}
        {hasContractorCompanyInformationChanged &&
          renderContractorCompanyInformation(assignment)}
        {hasBusinessAddressChanged && renderBusinessAddressSection(assignment)}
        {hasWorkerTypeSectionChanged && renderWorkerTypeSection(assignment)}
      </>
    );
  };

  if (
    !hasWorkerInfoChanged &&
    !hasWorkerHomeAddressChanged &&
    !hasBusinessAddressChanged &&
    !hasWorkerTypeSectionChanged &&
    !hasContractorCompanyInformationChanged &&
    !hasAssignmentTypeChanged
  )
    return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderGeneralInformation(oldData)}
      newData={renderGeneralInformation(newData)}
    />
  );
}
